import type { PlanV2IssueDispositionRecord } from './plan-v2-issues';
import { isSiteScopedWorkItem } from './plan-v2-work-item-groups';

/**
 * Putting ONE page back into the review belt.
 *
 * Until now the only way back was "Regenerate queue", which clears the whole
 * domain's triage — every page, every decision, the lot. An operator who wanted
 * a second look at one page had to throw away the other forty-four, so in
 * practice they did not go back at all.
 *
 * A re-open returns every decision that page owns to the issues queue: the work
 * items it produced, the cards put aside on it, and the row that hides the page
 * from the brief. It deliberately leaves the two kinds of row that merely
 * happen to be stamped with this page's id — a sent site-wide defect and a
 * muted check are decisions about the SITE, and taking them back because the
 * operator wanted another look at one page would be answering a question they
 * did not ask. See `utils/plan-v2-work-item-groups`.
 */
export interface PlanV2UrlReopenPlan {
  urlId: string;
  url: string;
  /** Rows to return to the issues queue, oldest decision first is irrelevant — order is stable by key. */
  issueKeys: string[];
  /** Total rows the re-open moves. */
  total: number;
  /** Of those, how many were accepted work. */
  workItemCount: number;
  /** Of those, how many were cards put aside (includes the ignored-page row). */
  dismissedCount: number;
  /** Whether the page itself was hidden from the brief. */
  pageWasIgnored: boolean;
  /** Site-wide / muted-check rows stamped with this page that the re-open leaves alone. */
  siteScopedKeptCount: number;
}

export function planPlanV2UrlReopen(
  urlId: string,
  url: string,
  records: Iterable<PlanV2IssueDispositionRecord> | null | undefined
): PlanV2UrlReopenPlan {
  const id = (urlId || '').trim();
  const plan: PlanV2UrlReopenPlan = {
    urlId: id,
    url: (url || '').trim(),
    issueKeys: [],
    total: 0,
    workItemCount: 0,
    dismissedCount: 0,
    pageWasIgnored: false,
    siteScopedKeptCount: 0
  };
  if (!id) return plan;

  for (const record of records ?? []) {
    if (!record || (record.urlId || '').trim() !== id) continue;
    if (isSiteScopedWorkItem(record)) {
      plan.siteScopedKeptCount += 1;
      continue;
    }
    const issueKey = (record.issueKey || '').trim();
    if (!issueKey) continue;
    plan.issueKeys.push(issueKey);
    if (record.queue === 'work_item') plan.workItemCount += 1;
    else plan.dismissedCount += 1;
    if (record.issueType === 'url_ignored') plan.pageWasIgnored = true;
  }

  plan.issueKeys.sort();
  plan.total = plan.issueKeys.length;
  return plan;
}

function pluralItems(count: number): string {
  return `${count} item${count === 1 ? '' : 's'}`;
}

/**
 * The sentence the operator agrees to.
 *
 * It leads with the count because that is the thing being agreed to, and it
 * names what is NOT moving, because a confirmation that only lists what it
 * takes teaches the operator nothing about what it leaves.
 */
export function planV2UrlReopenConfirmMessage(plan: PlanV2UrlReopenPlan): string {
  const parts: string[] = [];
  if (plan.workItemCount) {
    parts.push(`${plan.workItemCount} work item${plan.workItemCount === 1 ? '' : 's'}`);
  }
  if (plan.dismissedCount) {
    parts.push(`${plan.dismissedCount} put aside`);
  }
  const breakdown = parts.length > 1 ? ` (${parts.join(', ')})` : '';

  let message =
    `Re-open ${plan.url || 'this page'}?\n\n` +
    `This puts ${pluralItems(plan.total)}${breakdown} back into the Issues queue, ` +
    `so the page comes back into review.`;

  if (plan.pageWasIgnored) {
    message += `\n\nThe page is currently hidden from the brief. Re-opening shows it again.`;
  }

  if (plan.siteScopedKeptCount) {
    // These are the `sitewide::` and `muted::` rows. Both are keyed by check
    // code and both were stamped with this page only because it is where the
    // operator was standing, so both survive a re-open — and the sentence has
    // to cover both without naming a data type at someone.
    const n = plan.siteScopedKeptCount;
    message +=
      `\n\n${n} decision${n === 1 ? '' : 's'} about technical checks ` +
      `stay${n === 1 ? 's' : ''} as ${n === 1 ? 'it is' : 'they are'} — ` +
      `a check you sent or set aside applies across the site, not just to this page.`;
  }

  return message;
}

/** "Re-opened — 4 items back in the Issues queue." */
export function planV2UrlReopenResultMessage(count: number): string {
  return `Re-opened — ${pluralItems(count)} back in the Issues queue.`;
}

export const PLAN_V2_URL_REOPEN_NOTHING_TO_DO =
  'Nothing to re-open — this page has no decisions saved this period.';
